import React, { useEffect, useState, useRef } from 'react'
import Bentologo from "../../images/favicon.png"
import { useNavigate } from 'react-router-dom'
import { HiOutlineMenuAlt2 } from "react-icons/hi";
import { FiUser } from "react-icons/fi";
import apiClient from "../../utils/apiclient";

const Index = ({ isOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const [userDetails, setUserDetails] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchUser = () => {
      try {
        const token = localStorage.getItem("bentoAdmin");
        if (token) {
          const payload = token.split('.')[1];
          const decoded = JSON.parse(atob(payload));
          setUserDetails(decoded);
        }
      } catch (err) {
        console.error("Failed to decode user token", err);
      }
    };
    fetchUser();
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  }

  const confirmLogout = () => {
    localStorage.removeItem("bentoAdmin")
    localStorage.removeItem("bentoAdminExpiry")
    navigate("/login")
  }

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  }

  return (
    <div className='relative flex justify-between items-center z-50 bg-gray-950 border-b border-gray-800 h-12 px-2 md:px-4'>
      <div className='flex items-center gap-3'>
        {!isOpen && (
          <div className="flex items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="p-1 -ml-1 text-gray-400 hover:text-white transition-colors focus:outline-none"
              aria-label="Open Sidebar"
            >
              <HiOutlineMenuAlt2 className="text-2xl" />
            </button>
            <div className='flex items-center gap-3'>
              <img src={Bentologo} alt='Bento Box Logo' className='h-7 w-auto object-contain' />
              <h1 className='font-bold text-lg md:text-xl text-white hidden sm:block tracking-tight'>
                {(() => {
                  const roleId = localStorage.getItem("bentoAdminDetails");
                  return (roleId === '5' || roleId === 'support') 
                    ? "BentoBox Support Portal" 
                    : "BentoBox Admin Portal";
                })()}
              </h1>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 md:gap-4">
        <button
          className='text-white bg-blue-600 hover:bg-blue-700 px-2 py-1 md:px-4 md:py-2 rounded-lg transition-all font-medium text-xs md:text-sm shadow-sm whitespace-nowrap'
          onClick={() => navigate('/RiderDashboard')}
        >
          <span className="md:hidden">Riders</span>
          <span className="hidden md:inline">Rider Dashboard</span>
        </button>

        <button
          className='text-white bg-orange-500 hover:bg-orange-600 px-2 py-1 md:px-4 md:py-2 rounded-lg transition-all font-medium text-xs md:text-sm shadow-sm whitespace-nowrap'
          onClick={() => navigate('/refund-requests')}
        >
          <span className="md:hidden">Refunds</span>
          <span className="hidden md:inline">Refund Requests</span>
        </button>

        <button
          className='text-red-400 bg-red-500/10 border border-red-500/30 hover:bg-red-600 hover:text-white hover:border-red-600 px-2 py-1 md:px-5 md:py-2 rounded-lg transition-all font-medium text-xs md:text-sm shadow-sm whitespace-nowrap'
          onClick={handleLogoutClick}
        >
          Logout
        </button>

        <div className='relative flex items-center gap-2 border-l border-gray-800 pl-3 md:pl-4' ref={profileRef}>
          <button
            className='w-8 h-8 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-300 border border-gray-700 focus:outline-none transition-colors'
            onClick={() => setIsProfileOpen(!isProfileOpen)}
          >
            <FiUser className='text-lg' />
          </button>
          <div className='hidden sm:flex flex-col justify-center'>
            <span className='text-sm font-semibold text-gray-200 leading-tight'>
              {(() => {
                const roleId = localStorage.getItem("bentoAdminDetails");
                if (roleId === '5' || roleId === 'support') return "Support User";
                if (roleId === '4' || roleId === 'admin') return "Admin User";
                const name = userDetails?.name;
                if (name) return name;
                return "Admin User";
              })()}
            </span>
            <span className='text-[10px] text-gray-400 font-medium'>
              {userDetails?.number || userDetails?.sub || "Dashboard Access"}
            </span>
          </div>

          {/* Mobile Popup */}
          {isProfileOpen && (
            <div className='absolute top-full right-0 mt-3 w-48 bg-gray-900 border border-gray-800 rounded-xl shadow-2xl py-3 px-4 z-50 sm:hidden'>
              <div className='flex flex-col'>
                <span className='text-sm font-semibold text-gray-200'>
                  {(() => {
                    const roleId = localStorage.getItem("bentoAdminDetails");
                    if (roleId === '5' || roleId === 'support') return "Support User";
                    if (roleId === '4' || roleId === 'admin') return "Admin User";
                    const name = userDetails?.name;
                    if (name) return name;
                    return "Admin User";
                  })()}
                </span>
                <span className='text-xs text-gray-400 mt-1'>
                  {userDetails?.number || userDetails?.sub || "Dashboard Access"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-[90%] max-w-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Logout</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to log out of your account?</p>
            <div className="flex justify-end gap-3">
              <button 
                onClick={cancelLogout}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button 
                onClick={confirmLogout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Index
